import React, { useState, useEffect, useCallback } from 'react';
import './Stage6PCOL.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ----- Disclaimers (matches backend DISCLAIMERS dict) -----
const DISCLAIMER_GLOBAL = (
  'Estatel AI provides guidance to help you complete your PCOL claim. ' +
  'This is a document population and process guidance service only — ' +
  'not legal advice. All decisions, submissions, and payments are made ' +
  'by you directly on possessionclaim.gov.uk. If you are unsure of your ' +
  'legal position please consult a solicitor before proceeding.'
);

const DISCLAIMERS = {
  field_guidance: (
    'The values below are populated from your Estatel AI records. Verify ' +
    'each field carefully before entering it into PCOL. Estatel AI is not ' +
    'responsible for errors in your PCOL submission.'
  ),
  rent_schedule: (
    'The rent schedule must accurately reflect all payments due and ' +
    'received. You are responsible for verifying its accuracy before ' +
    'submitting to the court. This is not legal advice.'
  ),
  document_upload: (
    'You are responsible for ensuring all uploaded documents are correct, ' +
    'signed where required, and match your PCOL submission. Estatel AI ' +
    'assembles the bundle from the documents you provide.'
  ),
  bundle_download: (
    'Review the complete bundle carefully before serving. All N215 ' +
    'certificates of service must be signed by you before posting. ' +
    'This bundle is not a substitute for legal advice.'
  ),
};

// ----- Helpers -----
const safeFetchJson = async (url, opts = {}) => {
  const res = await fetch(url, { credentials: 'include', ...opts });
  const text = await res.text();
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch (_) { data = {}; }
  return { ok: res.ok, status: res.status, data };
};

const Banner = ({ kind = 'amber', testid, children }) => {
  const palette = kind === 'amber'
    ? { bg: '#fffbeb', border: '#fcd34d', color: '#78350f' }
    : { bg: '#eff6ff', border: '#93c5fd', color: '#1e3a8a' };
  return (
    <div data-testid={testid}
         style={{ background: palette.bg, border: `1px solid ${palette.border}`,
                  color: palette.color, padding: '0.75rem 1rem',
                  borderRadius: 6, fontSize: '0.88rem', lineHeight: 1.55,
                  margin: '0 0 0.85rem' }}>
      {children}
    </div>
  );
};

const StepDot = ({ status, label, idx }) => {
  const colors = {
    grey:  { bg: '#f1f5f9', dot: '#94a3b8', text: '#475569' },
    amber: { bg: '#fffbeb', dot: '#f59e0b', text: '#78350f' },
    green: { bg: '#ecfdf5', dot: '#10b981', text: '#065f46' },
  }[status] || { bg: '#f1f5f9', dot: '#94a3b8', text: '#475569' };
  return (
    <div data-testid={`pcol-step-${idx}`}
         style={{ flex: 1, padding: '0.55rem 0.7rem', background: colors.bg,
                  borderRadius: 8, display: 'flex', gap: 8, alignItems: 'center',
                  fontSize: '0.78rem', color: colors.text, fontWeight: 500 }}>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: colors.dot,
                     flexShrink: 0 }} />
      <span><strong style={{ marginRight: 4 }}>Step {idx}.</strong>{label}</span>
    </div>
  );
};

const ProgressTracker = ({ progress }) => {
  const steps = [
    { idx: 1, label: 'Pre-flight checklist completed',
      status: progress.checklist_completed ? 'green'
            : Object.values(progress.checklist || {}).some(Boolean) ? 'amber' : 'grey' },
    { idx: 2, label: 'Field guidance reviewed',
      status: progress.field_guidance_reviewed ? 'green' : 'grey' },
    { idx: 3, label: 'Rent schedule confirmed',
      status: progress.rent_schedule_confirmed ? 'green' : 'grey' },
    { idx: 4, label: 'Claim submitted on PCOL',
      status: progress.pcol_submitted_confirmed ? 'green' : 'grey' },
    { idx: 5, label: 'Documents uploaded',
      status: progress.documents_uploaded ? 'green'
            : Object.values(progress.uploaded_state || {}).some(Boolean) ? 'amber' : 'grey' },
    { idx: 6, label: 'Bundle assembled',
      status: progress.bundle_assembled ? 'green' : 'grey' },
  ];
  return (
    <div data-testid="pcol-progress-tracker"
         style={{ display: 'flex', gap: 6, marginBottom: '1rem', flexWrap: 'wrap' }}>
      {steps.map(s => <StepDot key={s.idx} {...s} />)}
    </div>
  );
};

// ============================================================================
// ITEM 3 — Pre-flight checklist
// ============================================================================
const Checklist = ({ caseId, progress, onSaved }) => {
  const initial = progress.checklist || {};
  const [boxes, setBoxes] = useState({
    has_account: !!initial.has_account,
    payment_card: !!initial.payment_card,
    notice_available: !!initial.notice_available,
    understand_guidance: !!initial.understand_guidance,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBoxes({
      has_account: !!initial.has_account,
      payment_card: !!initial.payment_card,
      notice_available: !!initial.notice_available,
      understand_guidance: !!initial.understand_guidance,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress.checklist_completed]);

  const allTicked = Object.values(boxes).every(Boolean);

  const submit = async () => {
    setSaving(true);
    try {
      const { ok } = await safeFetchJson(`${BACKEND_URL}/api/section8/pcol/checklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ case_id: caseId, ...boxes }),
      });
      if (ok) await onSaved();
    } finally { setSaving(false); }
  };

  if (progress.checklist_completed) {
    return (
      <div className="pcol-card" data-testid="pcol-checklist-done">
        <div className="pcol-card-header">
          <h3>Pre-flight checklist</h3>
          <span className="pcol-badge pcol-badge-green">Completed</span>
        </div>
      </div>
    );
  }

  const items = [
    { key: 'has_account',
      text: 'I have a PCOL account at possessionclaim.gov.uk. If not register before continuing.' },
    { key: 'payment_card',
      text: 'I have my payment card ready to pay the court fee online.' },
    { key: 'notice_available',
      text: 'I have the Section 8 notice available to refer to.' },
    { key: 'understand_guidance',
      text: 'I understand this guidance is not legal advice and I am submitting the claim myself.' },
  ];

  return (
    <div className="pcol-card" data-testid="pcol-checklist">
      <h3>Pre-flight checklist</h3>
      <p className="pcol-card-help">Tick all four to continue.</p>
      <ul className="pcol-checklist-list">
        {items.map(it => (
          <li key={it.key}>
            <label>
              <input type="checkbox"
                     checked={boxes[it.key]}
                     onChange={e => setBoxes(b => ({ ...b, [it.key]: e.target.checked }))}
                     data-testid={`pcol-check-${it.key}`} />
              <span>{it.text}</span>
            </label>
          </li>
        ))}
      </ul>
      <button type="button"
              className="pcol-btn pcol-btn-primary"
              disabled={!allTicked || saving}
              onClick={submit}
              data-testid="pcol-checklist-continue">
        {saving ? 'Saving…' : 'Continue'}
      </button>
    </div>
  );
};

// ============================================================================
// ITEM 5 v2 — Field guidance with copy buttons + Mark as Complete
// ============================================================================
const FieldGuidance = ({ caseId, sections, sectionCopies, progress,
                         onCopy, onMarkComplete }) => {
  const [openSecs, setOpenSecs] = useState(() =>
    Object.fromEntries((sections || []).map(s => [s.id, true])));
  const [marking, setMarking] = useState(false);

  const sectionStatus = (s) => {
    const copied = (sectionCopies[s.id] || []).length;
    if (copied === 0) return 'grey';
    if (copied < (s.fields || []).length) return 'amber';
    return 'green';
  };

  const handleCopy = async (sec, field) => {
    try { await navigator.clipboard.writeText(field.value || ''); } catch (_) {}
    onCopy(sec.id, field.id);
  };

  // All fields copied?
  const totalFields = (sections || []).reduce((n, s) => n + (s.fields || []).length, 0);
  const copiedCount = (sections || []).reduce(
    (n, s) => n + ((sectionCopies[s.id] || []).length), 0);
  const allCopied = totalFields > 0 && copiedCount >= totalFields;

  const markComplete = async () => {
    setMarking(true);
    try { await onMarkComplete(); } finally { setMarking(false); }
  };

  return (
    <div className="pcol-card" data-testid="pcol-field-guidance">
      <div className="pcol-card-header">
        <h3>PCOL field-by-field guidance</h3>
      </div>
      <Banner kind="amber" testid="pcol-disclaimer-fields">
        {DISCLAIMERS.field_guidance}
      </Banner>
      <p className="pcol-card-help">
        Open <a href="https://www.possessionclaim.gov.uk" target="_blank" rel="noopener noreferrer">
        possessionclaim.gov.uk</a> in another tab and copy each value
        below into the matching PCOL field as you go.
      </p>

      {(sections || []).map(sec => {
        const status = sectionStatus(sec);
        return (
          <div key={sec.id} className={`pcol-section pcol-section-${status}`}
               data-testid={`pcol-section-${sec.id}`}>
            <button type="button" className="pcol-section-head"
                    onClick={() => setOpenSecs(o => ({ ...o, [sec.id]: !o[sec.id] }))}
                    data-testid={`pcol-section-toggle-${sec.id}`}>
              <span>{sec.title}</span>
              <span className={`pcol-status pcol-status-${status}`}>
                {status === 'green' ? 'All copied'
                 : status === 'amber' ? `${(sectionCopies[sec.id] || []).length} of ${sec.fields.length}`
                 : 'Not copied'}
              </span>
            </button>
            {openSecs[sec.id] && (
              <div className="pcol-section-body">
                {(sec.fields || []).map(f => {
                  const copied = (sectionCopies[sec.id] || []).includes(f.id);
                  return (
                    <div key={f.id} className="pcol-field-row"
                         data-testid={`pcol-field-${sec.id}-${f.id}`}>
                      <div className="pcol-field-label">
                        {f.label}
                        {f.note && <div className="pcol-field-note">{f.note}</div>}
                      </div>
                      <div className="pcol-field-value">
                        <code>{f.value || <span className="pcol-empty">—</span>}</code>
                      </div>
                      <button type="button"
                              className={`pcol-btn pcol-btn-sm ${copied ? 'pcol-btn-copied' : ''}`}
                              onClick={() => handleCopy(sec, f)}
                              data-testid={`pcol-copy-${sec.id}-${f.id}`}>
                        {copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Spec Item 5 v2 — manual Mark as Complete confirmation. */}
      <div className="pcol-mark-row" data-testid="pcol-mark-complete-row">
        <div className="pcol-mark-progress">
          <strong>{copiedCount} of {totalFields}</strong> fields copied
          {!allCopied && <span> — copy every field before marking complete.</span>}
        </div>
        <button type="button"
                className="pcol-btn pcol-btn-primary"
                disabled={!allCopied || marking || progress.field_guidance_reviewed}
                onClick={markComplete}
                data-testid="pcol-mark-complete-btn">
          {progress.field_guidance_reviewed
            ? 'Marked complete ✓'
            : marking ? 'Saving…' : 'Mark as Complete'}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// ITEM 5 — Rent schedule
// ============================================================================
const RentSchedule = ({ caseId, onChanged }) => {
  const [data, setData] = useState(null);
  const [editingRows, setEditingRows] = useState(null);
  const [option, setOption] = useState(null);  // 'A' | 'B' | 'C'
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadedRows, setUploadedRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const { ok, data: d } = await safeFetchJson(
      `${BACKEND_URL}/api/section8/pcol/rent-schedule-data/${caseId}`);
    if (ok) {
      setData(d);
      // Prefer saved rows when present; else use auto-generated rows
      if (d.saved_rows && d.saved_rows.length) setEditingRows(d.saved_rows);
      else setEditingRows(d.rows || []);
    }
  }, [caseId]);

  useEffect(() => { reload(); }, [reload]);

  const updateCell = (idx, key, value) => {
    setEditingRows(rows => {
      const copy = rows.map(r => ({ ...r }));
      copy[idx] = { ...copy[idx], [key]: value };
      // Recompute running arrears from idx onward
      let running = idx === 0 ? 0 : parseFloat(copy[idx - 1].running_arrears || '0') || 0;
      for (let i = idx; i < copy.length; i++) {
        const due  = parseFloat(copy[i].amount_due || '0') || 0;
        const paid = parseFloat(copy[i].amount_paid || '0') || 0;
        running = running + (due - paid);
        copy[i].running_arrears = running.toFixed(2);
      }
      return copy;
    });
  };

  const addRow = () => {
    setEditingRows(rows => [...rows, {
      rent_due_date: '', amount_due: '0.00', amount_paid: '0.00',
      running_arrears: rows.length ? rows[rows.length - 1].running_arrears : '0.00',
    }]);
  };

  const removeRow = (idx) => {
    setEditingRows(rows => {
      const copy = rows.filter((_, i) => i !== idx);
      let running = 0;
      for (let i = 0; i < copy.length; i++) {
        const due  = parseFloat(copy[i].amount_due || '0') || 0;
        const paid = parseFloat(copy[i].amount_paid || '0') || 0;
        running = running + (due - paid);
        copy[i].running_arrears = running.toFixed(2);
      }
      return copy;
    });
  };

  const confirmSchedule = async (source) => {
    setBusy(true);
    try {
      const { ok } = await safeFetchJson(
        `${BACKEND_URL}/api/section8/pcol/rent-schedule/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ case_id: caseId, rows: editingRows, source }),
        });
      if (ok) {
        await reload();
        await onChanged();
      }
    } finally { setBusy(false); }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('case_id', caseId);
      fd.append('file', uploadFile);
      const res = await fetch(
        `${BACKEND_URL}/api/section8/pcol/rent-schedule/upload`,
        { method: 'POST', credentials: 'include', body: fd });
      const text = await res.text();
      const json = text ? JSON.parse(text) : {};
      if (res.ok) {
        setUploadedRows(json.rows || []);
        setEditingRows(json.rows && json.rows.length ? json.rows : (data?.rows || []));
      } else {
        window.alert(json.detail || 'Upload failed');
      }
    } finally { setBusy(false); }
  };

  if (!data) return <div className="pcol-card">Loading rent schedule…</div>;

  const downloadAuto = async () => {
    // Confirm using the auto-generated rows directly
    setBusy(true);
    try {
      const { ok, data: r } = await safeFetchJson(
        `${BACKEND_URL}/api/section8/pcol/rent-schedule/confirm`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ case_id: caseId, rows: data.rows, source: 'auto' }),
        });
      if (ok) {
        await reload();
        await onChanged();
        // Open the PDF
        window.open(`${BACKEND_URL}${r.rent_schedule_pdf_url}`, '_blank');
      }
    } finally { setBusy(false); }
  };

  const totals = (() => {
    let due = 0, paid = 0, last = 0;
    for (const r of (editingRows || [])) {
      due  += parseFloat(r.amount_due || '0') || 0;
      paid += parseFloat(r.amount_paid || '0') || 0;
      last  = parseFloat(r.running_arrears || '0') || last;
    }
    return { due: due.toFixed(2), paid: paid.toFixed(2), last: last.toFixed(2) };
  })();

  return (
    <div className="pcol-card" data-testid="pcol-rent-schedule">
      <div className="pcol-card-header">
        <h3>Rent schedule</h3>
        <span className={`pcol-badge ${data.rent_schedule_confirmed ? 'pcol-badge-green' : 'pcol-badge-amber'}`}
              data-testid="pcol-rent-schedule-status">
          {data.rent_schedule_confirmed ? 'Rent schedule confirmed' : 'Not confirmed'}
        </span>
      </div>
      <Banner kind="amber" testid="pcol-disclaimer-rent">{DISCLAIMERS.rent_schedule}</Banner>

      <p className="pcol-card-help">
        Pick one of the three options below. Once confirmed, the schedule
        will be included automatically in your court bundle.
      </p>

      <div className="pcol-rent-options">
        <button type="button"
                className={`pcol-option ${option === 'A' ? 'pcol-option-active' : ''}`}
                onClick={() => setOption('A')}
                data-testid="pcol-rent-option-A">
          <strong>Option A</strong>
          <span>Download pre-populated schedule from Estatel AI</span>
        </button>
        <button type="button"
                className={`pcol-option ${option === 'B' ? 'pcol-option-active' : ''}`}
                onClick={() => setOption('B')}
                data-testid="pcol-rent-option-B">
          <strong>Option B</strong>
          <span>Generate and edit from Estatel AI records</span>
        </button>
        <button type="button"
                className={`pcol-option ${option === 'C' ? 'pcol-option-active' : ''}`}
                onClick={() => setOption('C')}
                data-testid="pcol-rent-option-C">
          <strong>Option C</strong>
          <span>Upload your own schedule</span>
        </button>
      </div>

      <div className="pcol-rent-static-links">
        <a href={`${BACKEND_URL}/api/section8/pcol/rent-schedule/template.docx`}
           target="_blank" rel="noopener noreferrer"
           data-testid="pcol-template-blank">
          Download blank rent schedule template (Word)
        </a>
        <a href={`${BACKEND_URL}/api/section8/pcol/rent-schedule/example.pdf`}
           target="_blank" rel="noopener noreferrer"
           data-testid="pcol-template-example">
          Download example completed rent schedule (PDF)
        </a>
      </div>

      {option === 'A' && (
        <div className="pcol-rent-pane" data-testid="pcol-rent-pane-A">
          <p>Preview the auto-generated schedule below. Click <em>Confirm and Download</em> to lock it into your bundle.</p>
          <ScheduleTable rows={data.rows || []} readOnly />
          <div className="pcol-rent-actions">
            <button type="button" className="pcol-btn pcol-btn-primary"
                    disabled={busy} onClick={downloadAuto}
                    data-testid="pcol-rent-confirm-A">
              Confirm and Download
            </button>
          </div>
        </div>
      )}

      {option === 'B' && (
        <div className="pcol-rent-pane" data-testid="pcol-rent-pane-B">
          <p>Edit any cell. Running arrears recalculate automatically.</p>
          <ScheduleTable rows={editingRows || []}
                         onChange={updateCell}
                         onAddRow={addRow}
                         onRemoveRow={removeRow}
                         totals={totals} />
          <div className="pcol-rent-actions">
            <button type="button" className="pcol-btn pcol-btn-primary"
                    disabled={busy}
                    onClick={() => confirmSchedule('edit')}
                    data-testid="pcol-rent-confirm-B">
              Confirm Schedule
            </button>
            {data.rent_schedule_confirmed && (
              <a className="pcol-btn pcol-btn-link"
                 href={`${BACKEND_URL}/api/section8/pcol/rent-schedule/pdf/${caseId}`}
                 target="_blank" rel="noopener noreferrer"
                 data-testid="pcol-rent-download-B">
                Download as PDF
              </a>
            )}
          </div>
        </div>
      )}

      {option === 'C' && (
        <div className="pcol-rent-pane" data-testid="pcol-rent-pane-C">
          <p>Accepts PDF, Excel, Word, JPG or PNG. We will OCR images and reformat the data into the standard 4-column layout for you to review.</p>
          <input type="file"
                 accept=".pdf,.xlsx,.xls,.docx,.jpg,.jpeg,.png"
                 onChange={e => setUploadFile(e.target.files[0])}
                 data-testid="pcol-rent-upload-file" />
          <button type="button" className="pcol-btn pcol-btn-secondary"
                  disabled={busy || !uploadFile}
                  onClick={handleUpload}
                  data-testid="pcol-rent-upload-parse">
            {busy ? 'Parsing…' : 'Parse uploaded file'}
          </button>
          {uploadedRows !== null && (
            <>
              <p style={{ marginTop: '0.75rem' }}>
                {uploadedRows.length
                  ? 'Review the parsed rows below — edit any cell as needed before confirming.'
                  : 'No structured rows could be parsed. Use the editable table to enter the schedule manually.'}
              </p>
              <ScheduleTable rows={editingRows || []}
                             onChange={updateCell}
                             onAddRow={addRow}
                             onRemoveRow={removeRow}
                             totals={totals} />
              <div className="pcol-rent-actions">
                <button type="button" className="pcol-btn pcol-btn-primary"
                        disabled={busy}
                        onClick={() => confirmSchedule('upload')}
                        data-testid="pcol-rent-confirm-C">
                  Confirm Schedule
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ScheduleTable = ({ rows, onChange, onAddRow, onRemoveRow, totals, readOnly }) => (
  <div className="pcol-rent-table-wrap">
    <table className="pcol-rent-table" data-testid="pcol-rent-table">
      <thead>
        <tr>
          <th>Rent Due Date</th>
          <th>Amount Due</th>
          <th>Amount Paid</th>
          <th>Running Arrears</th>
          {!readOnly && <th></th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td>{readOnly
              ? r.rent_due_date
              : <input value={r.rent_due_date || ''}
                       onChange={e => onChange(i, 'rent_due_date', e.target.value)}
                       data-testid={`pcol-rent-row-${i}-date`} />}</td>
            <td>{readOnly
              ? r.amount_due
              : <input type="number" step="0.01" value={r.amount_due || ''}
                       onChange={e => onChange(i, 'amount_due', e.target.value)}
                       data-testid={`pcol-rent-row-${i}-due`} />}</td>
            <td>{readOnly
              ? r.amount_paid
              : <input type="number" step="0.01" value={r.amount_paid || ''}
                       onChange={e => onChange(i, 'amount_paid', e.target.value)}
                       data-testid={`pcol-rent-row-${i}-paid`} />}</td>
            <td>{r.running_arrears}</td>
            {!readOnly && (
              <td>
                <button type="button" className="pcol-btn-icon"
                        onClick={() => onRemoveRow(i)}
                        data-testid={`pcol-rent-row-${i}-remove`}>×</button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
      {totals && (
        <tfoot>
          <tr>
            <th>Total</th>
            <th>{totals.due}</th>
            <th>{totals.paid}</th>
            <th>{totals.last}</th>
            {!readOnly && <th></th>}
          </tr>
        </tfoot>
      )}
    </table>
    {!readOnly && (
      <button type="button" className="pcol-btn pcol-btn-link"
              onClick={onAddRow} data-testid="pcol-rent-add-row">
        + Add row
      </button>
    )}
  </div>
);

// ============================================================================
// ITEM 6 — Submit on PCOL checkpoint
// ============================================================================
const SubmitCheckpoint = ({ caseId, progress, onChanged }) => {
  const [confirmed, setConfirmed] = useState(progress.pcol_submitted_confirmed);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setConfirmed(progress.pcol_submitted_confirmed); },
    [progress.pcol_submitted_confirmed]);

  const save = async (val) => {
    setBusy(true);
    try {
      const { ok } = await safeFetchJson(
        `${BACKEND_URL}/api/section8/pcol/confirm-submitted`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ case_id: caseId, confirmed: val }),
        });
      if (ok) { setConfirmed(val); await onChanged(); }
    } finally { setBusy(false); }
  };

  return (
    <div className="pcol-card pcol-submit-card" data-testid="pcol-submit-checkpoint">
      <h3>Submit your claim on PCOL</h3>
      <p>
        Using the field values from <strong>Tab 2 — Field Guidance</strong>, log into{' '}
        <a href="https://www.possessionclaim.gov.uk" target="_blank" rel="noopener noreferrer">
          possessionclaim.gov.uk
        </a>{' '}
        and complete your claim:
      </p>
      <ol className="pcol-step-list">
        <li>Complete all claim fields using the values from Tab 2</li>
        <li>Pay the court fee online</li>
        <li>Note your claim number</li>
        <li>Download your N5 from PCOL</li>
        <li>Download your N119 from PCOL</li>
        <li>Download the rent statement from PCOL</li>
        <li>Return here and tick the confirmation below</li>
      </ol>
      <label className="pcol-confirm">
        <input type="checkbox" checked={confirmed}
               disabled={busy}
               onChange={e => save(e.target.checked)}
               data-testid="pcol-submit-confirm-checkbox" />
        <span>
          I have submitted the claim on possessionclaim.gov.uk, paid the
          court fee, and downloaded my N5, N119, and rent statement from PCOL.
        </span>
      </label>
    </div>
  );
};

// ============================================================================
// ITEM 8 v2 — Document upload + N5 AI extraction (4 fields incl. court address)
// ============================================================================
const DocumentUpload = ({ caseId, caseData, progress, onChanged }) => {
  const [claimNum, setClaimNum]         = useState(progress.claim_number   || '');
  const [courtName, setCourtName]       = useState(progress.court_name     || '');
  const [courtAddress, setCourtAddress] = useState(progress.court_address  || '');
  const [savingDetails, setSavingDetails] = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null);
  const [extractAlert, setExtractAlert]   = useState(null);

  useEffect(() => {
    setClaimNum(progress.claim_number || '');
    setCourtName(progress.court_name || '');
    setCourtAddress(progress.court_address || '');
  }, [progress.claim_number, progress.court_name, progress.court_address]);

  const saveDetails = async () => {
    setSavingDetails(true);
    try {
      await safeFetchJson(`${BACKEND_URL}/api/section8/pcol/save-claim-details`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseId,
          pcol_claim_number: claimNum,
          court_name: courtName,
          court_address: courtAddress,
        }),
      });
      await onChanged();
    } finally { setSavingDetails(false); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setUploadingKind(kind);
    try {
      const fd = new FormData();
      fd.append('case_id', caseId);
      fd.append('doc_kind', kind);
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/section8/pcol/upload-document`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : {};
      if (res.ok && kind === 'n5_issued' && data.extracted) {
        const ex = data.extracted;
        // Spec Item 8 v2 — N5 extraction yields 4 fields. Auto-populate
        // empty fields; flag conflicts for manual resolution.
        const conflicts = {};
        const autoUpdates = {};
        if (ex.claim_number) {
          if (claimNum && ex.claim_number !== claimNum) conflicts.claim_number = true;
          else if (!claimNum) { autoUpdates.pcol_claim_number = ex.claim_number;
                                setClaimNum(ex.claim_number); }
        }
        if (ex.court_name) {
          if (courtName && ex.court_name !== courtName) conflicts.court_name = true;
          else if (!courtName) { autoUpdates.court_name = ex.court_name;
                                  setCourtName(ex.court_name); }
        }
        if (ex.court_address) {
          if (courtAddress && ex.court_address !== courtAddress) conflicts.court_address = true;
          else if (!courtAddress) { autoUpdates.court_address = ex.court_address;
                                     setCourtAddress(ex.court_address); }
        }
        if (ex.issued_date) autoUpdates.pcol_issued_date = ex.issued_date;

        if (Object.keys(autoUpdates).length) {
          await safeFetchJson(`${BACKEND_URL}/api/section8/pcol/save-claim-details`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ case_id: caseId, ...autoUpdates }),
          });
        }
        if (Object.keys(conflicts).length) {
          setExtractAlert({
            entered: {
              claim_number:  claimNum,
              court_name:    courtName,
              court_address: courtAddress,
            },
            extracted: {
              claim_number:  ex.claim_number,
              court_name:    ex.court_name,
              court_address: ex.court_address,
              issued_date:   ex.issued_date,
            },
            conflicts,
          });
        }
      }
      await onChanged();
    } finally { setUploadingKind(null); }
  };

  const acceptExtracted = async () => {
    if (!extractAlert) return;
    const ex = extractAlert.extracted;
    await safeFetchJson(`${BACKEND_URL}/api/section8/pcol/save-claim-details`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        case_id: caseId,
        pcol_claim_number: ex.claim_number  || claimNum,
        court_name:        ex.court_name    || courtName,
        court_address:     ex.court_address || courtAddress,
        pcol_issued_date:  ex.issued_date,
      }),
    });
    if (ex.claim_number)  setClaimNum(ex.claim_number);
    if (ex.court_name)    setCourtName(ex.court_name);
    if (ex.court_address) setCourtAddress(ex.court_address);
    setExtractAlert(null);
    await onChanged();
  };

  const slot = (kind, label, accept, helper) => {
    const uploaded = !!progress.uploaded_state?.[kind];
    return (
      <div key={kind} className={`pcol-upload-slot ${uploaded ? 'pcol-upload-done' : ''}`}
           data-testid={`pcol-upload-${kind}`}>
        <div className="pcol-upload-head">
          <strong>{label}</strong>
          <span className={`pcol-status pcol-status-${uploaded ? 'green' : 'grey'}`}>
            {uploaded ? 'Uploaded' : 'Not uploaded'}
          </span>
        </div>
        {helper && <p className="pcol-upload-help">{helper}</p>}
        <input type="file" accept={accept}
               disabled={uploadingKind === kind}
               onChange={e => upload(kind, e.target.files[0])}
               data-testid={`pcol-upload-${kind}-input`} />
        {uploadingKind === kind && <span className="pcol-uploading">Uploading…</span>}
      </div>
    );
  };

  const useExistingSchedule = progress.rent_schedule_confirmed;

  return (
    <div className="pcol-card" data-testid="pcol-document-upload">
      <h3>Upload documents from PCOL</h3>
      <Banner kind="amber" testid="pcol-disclaimer-uploads">
        {DISCLAIMERS.document_upload}
      </Banner>

      <div className="pcol-claim-grid">
        <div>
          <label>Enter your claim number from PCOL</label>
          <input type="text" value={claimNum}
                 onChange={e => setClaimNum(e.target.value)}
                 data-testid="pcol-claim-number-input" />
        </div>
        <div>
          <label>Enter the court name shown on your PCOL confirmation</label>
          <input type="text" value={courtName}
                 onChange={e => setCourtName(e.target.value)}
                 data-testid="pcol-court-name-input" />
        </div>
        <div>
          <label>Enter the full court address</label>
          <input type="text" value={courtAddress}
                 onChange={e => setCourtAddress(e.target.value)}
                 placeholder="e.g. Thomas More Building, Royal Courts of Justice, Strand, London WC2A 2LL"
                 data-testid="pcol-court-address-input" />
          <small className="pcol-helper-small">
            This will be extracted automatically from your uploaded N5 if available.
          </small>
        </div>
        <button type="button" className="pcol-btn pcol-btn-secondary pcol-claim-save-btn"
                disabled={savingDetails}
                onClick={saveDetails}
                data-testid="pcol-save-claim-details">
          {savingDetails ? 'Saving…' : 'Save claim details'}
        </button>
      </div>

      {extractAlert && (
        <div className="pcol-extract-alert" data-testid="pcol-extract-alert">
          <p>
            The values extracted from your N5 do not match what you entered.
            Which is correct?
          </p>
          <ul className="pcol-extract-list">
            {extractAlert.conflicts.claim_number && (
              <li data-testid="pcol-conflict-claim_number">
                <strong>Claim number:</strong> entered <code>{extractAlert.entered.claim_number}</code> /
                extracted <code>{extractAlert.extracted.claim_number}</code>
              </li>
            )}
            {extractAlert.conflicts.court_name && (
              <li data-testid="pcol-conflict-court_name">
                <strong>Court name:</strong> entered <code>{extractAlert.entered.court_name}</code> /
                extracted <code>{extractAlert.extracted.court_name}</code>
              </li>
            )}
            {extractAlert.conflicts.court_address && (
              <li data-testid="pcol-conflict-court_address">
                <strong>Court address:</strong> entered <code>{extractAlert.entered.court_address}</code> /
                extracted <code>{extractAlert.extracted.court_address}</code>
              </li>
            )}
          </ul>
          <div className="pcol-extract-actions">
            <button type="button" className="pcol-btn pcol-btn-primary"
                    onClick={acceptExtracted}
                    data-testid="pcol-extract-use-extracted">
              Use extracted values
            </button>
            <button type="button" className="pcol-btn pcol-btn-secondary"
                    onClick={() => setExtractAlert(null)}
                    data-testid="pcol-extract-keep-entered">
              Keep my entries
            </button>
          </div>
        </div>
      )}

      <div className="pcol-upload-grid">
        {slot('n5_issued',          'N5 (issued by PCOL)',   '.pdf,.jpg,.jpeg,.png')}
        {slot('n119_issued',        'N119 (issued by PCOL)', '.pdf,.jpg,.jpeg,.png')}
        {slot('pcol_rent_statement','PCOL rent statement',   '.pdf,.jpg,.jpeg,.png',
              'This is the rent statement PCOL generates showing the latest rent due date and any payment received. It is separate from your full arrears schedule.')}
        <div className={`pcol-upload-slot ${progress.uploaded_state?.agent_rent_schedule ? 'pcol-upload-done' : ''}`}
             data-testid="pcol-upload-agent_rent_schedule">
          <div className="pcol-upload-head">
            <strong>Agent rent schedule</strong>
            <span className={`pcol-status pcol-status-${progress.uploaded_state?.agent_rent_schedule ? 'green' : 'grey'}`}>
              {progress.uploaded_state?.agent_rent_schedule ? 'Uploaded' : 'Not uploaded'}
            </span>
          </div>
          {useExistingSchedule && (
            <p className="pcol-upload-help" data-testid="pcol-using-confirmed-schedule">
              <strong>Using the rent schedule you already confirmed</strong> in Tab 3.
              No further upload required.
            </p>
          )}
          {!useExistingSchedule && (
            <>
              <p className="pcol-upload-help">Confirm a rent schedule in Tab 3, or upload one (PDF / Excel / Word / JPG / PNG):</p>
              <input type="file"
                     accept=".pdf,.xlsx,.xls,.docx,.jpg,.jpeg,.png"
                     disabled={uploadingKind === 'agent_rent_schedule_upload'}
                     onChange={e => upload('agent_rent_schedule_upload', e.target.files[0])}
                     data-testid="pcol-upload-agent_rent_schedule-input" />
            </>
          )}
        </div>
        {slot('tenancy_agreement', 'Tenancy agreement', '.pdf,.jpg,.jpeg,.png',
              'In a future update this will auto-populate from the property document store.')}
      </div>
    </div>
  );
};

// ============================================================================
// ITEM 9 — Bundle assembly + download
// ============================================================================
const BundleAssembly = ({ caseId, progress, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [error, setError] = useState(null);

  const ready = progress.documents_uploaded;

  const assemble = async () => {
    setBusy(true); setError(null);
    try {
      const { ok, data } = await safeFetchJson(
        `${BACKEND_URL}/api/section8/pcol/assemble-bundle/${caseId}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
        });
      if (!ok) {
        setError(data.detail || 'Bundle assembly failed.');
        return;
      }
      setBundle(data);
      await onChanged();
    } finally { setBusy(false); }
  };

  // Static bundle order (matches Item 9 spec)
  const docOrder = [
    { order: 1, label: 'Court covering letter' },
    { order: 2, label: 'N215 — Bundle service' },
    { order: 3, label: 'N5 (issued by PCOL)' },
    { order: 4, label: 'N119 (issued by PCOL)' },
    { order: 5, label: 'PCOL rent statement' },
    { order: 6, label: 'Agent rent schedule' },
    { order: 7, label: 'Section 8 notice (Form 3A)' },
    { order: 8, label: 'Tenancy agreement' },
    { order: 9, label: 'N215 — Notice service' },
  ];

  return (
    <div className="pcol-card pcol-bundle-card" data-testid="pcol-bundle">
      <div className="pcol-bundle-header">
        <h3>Bundle assembly</h3>
      </div>
      <div className="pcol-bundle-body">
        <Banner kind="amber" testid="pcol-disclaimer-bundle">
          {DISCLAIMERS.bundle_download}
        </Banner>

        <p className="pcol-card-help">Bundle order:</p>
        <ol className="pcol-bundle-list">
          {docOrder.map(d => (
            <li key={d.order}>{d.label}</li>
          ))}
        </ol>

      {!ready && (
        <Banner kind="info" testid="pcol-bundle-not-ready">
          Complete every upload and enter the claim number / court name to enable bundle assembly.
        </Banner>
      )}

      {ready && !progress.bundle_assembled && (
        <button type="button" className="pcol-btn pcol-btn-primary"
                disabled={busy}
                onClick={assemble}
                data-testid="pcol-assemble-bundle">
          {busy ? 'Assembling…' : 'Assemble PCOL bundle'}
        </button>
      )}

      {error && <Banner kind="amber" testid="pcol-bundle-error">{error}</Banner>}

      {progress.bundle_assembled && (
        <div className="pcol-bundle-done" data-testid="pcol-bundle-done">
          <Banner kind="amber" testid="pcol-bundle-action-alert">
            <strong>Before posting this bundle you must:</strong>
            <ol style={{ margin: '0.4rem 0 0 1.1rem' }}>
              <li>Sign the N215 bundle service certificate and enter the date and method of service by hand</li>
              <li>Sign the N215 notice service certificate if not already signed</li>
              <li>Make two copies — one for the court and one for the tenant</li>
            </ol>
          </Banner>
          <div className="pcol-download-grid">
            <a className="pcol-btn pcol-btn-primary"
               href={`${BACKEND_URL}/api/section8/document-pdf/${caseId}/pcol_bundle`}
               target="_blank" rel="noopener noreferrer"
               data-testid="pcol-download-merged">
              Download complete bundle (merged PDF)
            </a>
          </div>
          <details className="pcol-individual-downloads">
            <summary>Individual document downloads</summary>
            <ul>
              {(bundle?.documents || []).map(d => (
                <li key={d.order}>
                  <a href={`${BACKEND_URL}${d.url}`} target="_blank" rel="noopener noreferrer"
                     data-testid={`pcol-doc-${d.order}`}>
                    {d.order}. {d.label}
                  </a>
                </li>
              ))}
              {!bundle && (
                <li><em>Re-assemble the bundle to refresh individual links.</em></li>
              )}
            </ul>
          </details>
          <button type="button" className="pcol-btn pcol-btn-link"
                  onClick={assemble} disabled={busy}
                  data-testid="pcol-reassemble">
            {busy ? 'Re-assembling…' : 'Re-assemble bundle'}
          </button>
        </div>
      )}
      </div>
    </div>
  );
};

// ============================================================================
// ITEM 2 — Tab bar (sequential locking, status colours)
// ============================================================================
const TabBar = ({ tabs, active, setActive }) => (
  <div className="pcol-tabbar" data-testid="pcol-tabbar" role="tablist">
    {tabs.map(t => {
      const cls = `pcol-tab pcol-tab-${t.status} ${active === t.id ? 'pcol-tab-active' : ''} ${t.locked ? 'pcol-tab-locked' : ''}`;
      return (
        <button key={t.id} type="button" role="tab" className={cls}
                disabled={t.locked}
                aria-selected={active === t.id}
                onClick={() => !t.locked && setActive(t.id)}
                data-testid={`pcol-tab-${t.id}`}>
          <span className="pcol-tab-num">{t.num}</span>
          <span className="pcol-tab-label">{t.label}</span>
          <span className={`pcol-tab-status pcol-tab-status-${t.status}`}>
            {t.locked ? 'Locked' : t.status === 'green' ? 'Complete'
              : t.status === 'amber' ? 'In progress' : 'Not started'}
          </span>
        </button>
      );
    })}
  </div>
);

// ============================================================================
// MAIN PCOL ORCHESTRATOR — Tabbed (Spec Item 2 v2)
// ============================================================================
export default function Stage6PCOL({ caseId, caseData, onChanged }) {
  const [progress, setProgress] = useState(null);
  const [guidance, setGuidance] = useState(null);
  const [activeTab, setActiveTab] = useState('checklist');

  const reload = useCallback(async () => {
    const { ok, data } = await safeFetchJson(
      `${BACKEND_URL}/api/section8/pcol/progress/${caseId}`);
    if (ok) setProgress(data);
  }, [caseId]);

  const reloadGuidance = useCallback(async () => {
    const { ok, data } = await safeFetchJson(
      `${BACKEND_URL}/api/section8/pcol/field-guidance/${caseId}`);
    if (ok) setGuidance(data);
  }, [caseId]);

  useEffect(() => { reload(); reloadGuidance(); }, [reload, reloadGuidance]);

  const fullReload = useCallback(async () => {
    await reload();
    await reloadGuidance();
    if (onChanged) await onChanged();
  }, [reload, reloadGuidance, onChanged]);

  const handleCopy = async (sectionId, fieldId) => {
    await safeFetchJson(`${BACKEND_URL}/api/section8/pcol/track-copy`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ case_id: caseId, section_id: sectionId, field_id: fieldId }),
    });
    await reload();
  };

  const handleMarkFieldGuidance = async () => {
    await safeFetchJson(`${BACKEND_URL}/api/section8/pcol/mark-field-guidance-complete`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ case_id: caseId }),
    });
    await fullReload();
    setActiveTab('rent_schedule');
  };

  // Spec Item 2 v2 — sequential locking + grey/amber/green per tab.
  // NB: tabs computed every render (cheap; no memo). Hook order is stable.
  const tabs = (() => {
    if (!progress) return [];
    const checklistAny  = Object.values(progress.checklist || {}).some(Boolean);
    const uploadsAny    = Object.values(progress.uploaded_state || {}).some(Boolean);
    const claimDetailsAny = !!(progress.claim_number || progress.court_name || progress.court_address);
    return [
      {
        id: 'checklist', num: 1, label: 'Checklist',
        status: progress.checklist_completed ? 'green' : checklistAny ? 'amber' : 'grey',
        locked: false,
      },
      {
        id: 'field_guidance', num: 2, label: 'Field Guidance',
        status: progress.field_guidance_reviewed ? 'green'
              : (progress.section_copies && Object.keys(progress.section_copies).length) ? 'amber' : 'grey',
        locked: !progress.checklist_completed,
      },
      {
        id: 'rent_schedule', num: 3, label: 'Rent Schedule',
        status: progress.rent_schedule_confirmed ? 'green' : 'grey',
        locked: !(progress.checklist_completed && progress.field_guidance_reviewed),
      },
      {
        id: 'submit', num: 4, label: 'Submit on PCOL',
        status: progress.pcol_submitted_confirmed ? 'green' : 'grey',
        locked: !(progress.checklist_completed && progress.field_guidance_reviewed
                  && progress.rent_schedule_confirmed),
      },
      {
        id: 'upload', num: 5, label: 'Upload Documents',
        status: progress.documents_uploaded ? 'green'
              : (uploadsAny || claimDetailsAny) ? 'amber' : 'grey',
        locked: !progress.pcol_submitted_confirmed,
      },
      {
        id: 'bundle', num: 6, label: 'Bundle',
        status: progress.bundle_assembled ? 'green' : 'grey',
        locked: !progress.documents_uploaded,
      },
    ];
  })();

  // If active tab becomes locked (e.g. user reset something), bounce to first unlocked.
  // useEffect MUST be called unconditionally before any early return.
  const tabsLockKey = tabs.map(t => `${t.id}:${t.locked ? 1 : 0}`).join('|');
  useEffect(() => {
    const cur = tabs.find(t => t.id === activeTab);
    if (cur && cur.locked) {
      const firstUnlocked = tabs.find(t => !t.locked);
      if (firstUnlocked) setActiveTab(firstUnlocked.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tabsLockKey]);

  if (!progress) return <div className="pcol-card">Loading PCOL workflow…</div>;

  return (
    <div className="pcol-root" data-testid="stage-6-pcol">
      {/* Spec Item 3 — global persistent disclaimer on every tab */}
      <Banner kind="amber" testid="pcol-disclaimer-global">{DISCLAIMER_GLOBAL}</Banner>

      <ProgressTracker progress={progress} />

      <TabBar tabs={tabs} active={activeTab} setActive={setActiveTab} />

      <div className="pcol-tab-panel" data-testid={`pcol-panel-${activeTab}`}>
        {activeTab === 'checklist' && (
          <Checklist caseId={caseId} progress={progress}
                     onSaved={async () => { await fullReload(); setActiveTab('field_guidance'); }} />
        )}

        {activeTab === 'field_guidance' && guidance && (
          <FieldGuidance caseId={caseId}
                         sections={guidance.sections}
                         sectionCopies={progress.section_copies}
                         progress={progress}
                         onCopy={handleCopy}
                         onMarkComplete={handleMarkFieldGuidance} />
        )}

        {activeTab === 'rent_schedule' && (
          <RentSchedule caseId={caseId}
                         onChanged={async () => { await fullReload(); }} />
        )}

        {activeTab === 'submit' && (
          <SubmitCheckpoint caseId={caseId} progress={progress}
                             onChanged={async () => {
                               await fullReload();
                               // auto-advance once confirmed
                             }} />
        )}

        {activeTab === 'upload' && (
          <DocumentUpload caseId={caseId} caseData={caseData}
                           progress={progress}
                           onChanged={fullReload} />
        )}

        {activeTab === 'bundle' && (
          <BundleAssembly caseId={caseId} progress={progress}
                           onChanged={fullReload} />
        )}
      </div>
    </div>
  );
}
